import React from 'react'
import { BasicContextState, CacheReset, ContextConfig, GraphContextState, InfiniteScrollConfig, VersionedContextState } from './context.d'
import { AnyAction, Dispatch, LoadDetailsAction, LoadIdsAction,
    ApiRequestFailedAction, ApiRequestSucceededAction, PrimaryAction, ClearCacheAction, FullApiResponseAction,
    ApiResponseAction,
    ActionKey } from './action/Action.d'
import { actionKey, LOAD_DETAILS_ACTION_TYPES, LOAD_IDS_ACTION_TYPES } from './action/action'
import { InfiniteScrollContextState } from '../common/InfiniteScroll.d'
import { AppContextState } from '../contexts/AppContext.d'
import { DispatchToApi } from './dispatch.d'
import { AUTOSAVE_INTERVAL_FAILED } from '../Write/config'
import { GraphData } from '../common/graph/Graph.d'
import { reportStaleResponse } from './apiLog'

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === 'object' && v !== null && !Array.isArray(v)

export const defaultContextConfig: InfiniteScrollConfig & ContextConfig = {
    // context: null,
    contextDataLocation: [],
    localStorageLocation: 'resource',
    retainedOnCacheClear: {},
    idField: 'id',
    apiEndpointIds: '/resources',
    apiEndpointDetails: '/resource',
    defaultState: {},
    freshState: {
        failed: {},
        inProgress: {}
    },
    actionTypes: {
        loadDetails: [],
        loadIds: []
    },

    renderThumbnail: id => <div key="id" style={ { height: '50px' } }>Item #{id}</div>
}

export const resolveFreshState = <S extends BasicContextState>(clear: CacheReset<S>, state: S, appState?: AppContextState): S =>
    typeof clear === 'function' ? clear(state, appState) : clear

/** If appState.dataVersion is set to this specific value, it will trigger all other contexts to clear their states. */
export const FORCE_CACHE_CLEAR_VERSION = -1

export const initialiseState = <S extends BasicContextState = BasicContextState>(
    config: ContextConfig<S>,
    appState: AppContextState
): () => S =>
    () => {
        // Load data from local storage if available
        const startState = localStorage.getItem(config.localStorageLocation)
            ? JSON.parse(localStorage.getItem(config.localStorageLocation) || '{}') as S
            : { ...defaultContextConfig.defaultState, ...config.defaultState }
        // Reset the state as per the context config
        const freshState: S = appState?.offlineMode
            ? resolveFreshState(defaultContextConfig.freshState, startState, appState) as S
            : {
                ...defaultContextConfig.freshState,
                ...resolveFreshState(config.freshState, startState, appState)
            }
        if (appState.dataVersion?.latest === FORCE_CACHE_CLEAR_VERSION) {
            return resolveFreshState(config.retainedOnCacheClear, startState, appState)
        }
        return { ...startState, ...freshState, actionStatus: {} }
    }

export const checkCacheValidity = (state: VersionedContextState, dispatch: Dispatch<PrimaryAction>, appState: AppContextState): void => {
    if (!state.dataVersion && appState.dataVersion?.latest) {
        dispatch({
            type: 'RECEIVE_DATA_VERSION',
            version: appState.dataVersion.latest,
            redispatch: dispatch
        })
    } else if (state.dataVersion && appState.dataVersion?.latest && (
        state.dataVersion < appState.dataVersion.latest || (
            appState.dataVersion.latest === FORCE_CACHE_CLEAR_VERSION && state.dataVersion !== FORCE_CACHE_CLEAR_VERSION))) {
        dispatch({
            type: 'CLEAR_CACHE',
            version: appState.dataVersion?.latest,
            redispatch: dispatch
        })
    }
}

export const markActionStatus = <S extends BasicContextState = BasicContextState>(
    state: S, action: ActionKey | ApiResponseAction, status: 'querying' | 'complete'
): S => ({
    ...state,
    actionStatus: {
        ...state.actionStatus,
        [actionKey(action)]: status
    }
})

/** Returns true if the API response is strictly older than the item already in state.
 *  Uses Date.parse rather than lexical comparison to be immune to ISO-8601 format drift. */
const isStaleApiResponse = (location: string, itemId: string, existing: unknown, item: Record<string, unknown>): boolean => {
    const existingMod = existing && typeof existing === 'object'
      && 'modified' in existing && typeof existing.modified === 'string'
        ? existing.modified
        : null
    const itemMod = typeof item.modified === 'string' ? item.modified : null
    if (!existingMod || !itemMod) return false
    const existingTimestamp = Date.parse(existingMod)
    const itemTimestamp = Date.parse(itemMod)
    if (Number.isNaN(existingTimestamp) || Number.isNaN(itemTimestamp)) return false
    if (itemTimestamp >= existingTimestamp) return false
    // eslint-disable-next-line no-console
    console.warn(`API returned older data for ${location} ${itemId}:`
      + ` state has modified=${existingMod}, API returned modified=${itemMod}.`
      + ` API response status=${'status' in item ? String(item.status) : ''}.`
      + ' Full API item:', item)
    reportStaleResponse(location, itemId, existingMod, itemMod, 'status' in item ? item.status : undefined)
    return true
}

const treatDetailsRequestAction = <S extends BasicContextState = BasicContextState>(
    state: S, action: FullApiResponseAction<LoadDetailsAction>, config = defaultContextConfig) => {
    const items = Array.isArray(action.apiResponse.data)
        ? action.apiResponse.data
        : [action.apiResponse.data]

    if (!config.contextDataLocation[0]) {
        throw Error('No contextDataLocation found')
    }
    state[config.contextDataLocation[0]] = state[config.contextDataLocation[0]] || {}
    for (const item of items) {
        if (!isRecord(item)) {
            console.error(`Unexpected item in API response: ${String(item)}`)
            continue
        }
        if (!item[config.idField]) {
            console.error(`No '${config.idField}' field in API response object`)
            continue
        }
        const itemId = String(item[config.idField])
        const existing: unknown = state[config.contextDataLocation[0]][itemId]
        // unconfirmed fix for entries reverting after tab away
        if (isStaleApiResponse(config.localStorageLocation, itemId, existing, item)) continue
        state[config.contextDataLocation[0]][itemId] = item
        for (const field in config.secondaryIdFields) {
            if (!item[field]) {
                continue
            }
            if (!(config.secondaryIdFields[field] in state)) {
                state[config.secondaryIdFields[field]] = {}
            }
            state[config.secondaryIdFields[field]][item[field]] = item[config.idField]
        }
    }

    if (action.sourceAction?.items && items.length < action.sourceAction.items.length) {
        for (const idNotReturned of action.sourceAction.items) {
            if (!items.some(i => typeof i === 'object' && i?.[config.idField] === idNotReturned)) {
                delete state[config.contextDataLocation[0]][idNotReturned.toString()]
            }
        }
    }

    delete state.inProgress?.[actionKey(action)]
    return markActionStatus(state, action, 'complete')
}

const treatIdsRequestAction = <S extends InfiniteScrollContextState = InfiniteScrollContextState>(
    state: S, action: FullApiResponseAction<LoadIdsAction>) => {
    const apiResponseData = action.apiResponse.data as number | number[]
    const apiResponse: number[] = Array.isArray(apiResponseData)
        ? apiResponseData
        : [apiResponseData]
    const key = actionKey(action)

    state.idsByQuery = state.idsByQuery || {}
    state.idsByQuery[key] = state.idsByQuery[key] || []
    // Append to the already-known IDs, unless the page was refreshed since data was last retrieved
    state.idsByQuery[key] = state.actionStatus?.[key]
        ? state.idsByQuery[key].concat(apiResponse)
        : apiResponse

    delete state.inProgress?.[key]
    return markActionStatus(state, key, action.apiResponse.queryComplete ? 'complete' : 'querying')
}

const clearSiteCaches = <S extends VersionedContextState = VersionedContextState>(
    state: S, action: ClearCacheAction, contextConfig: ContextConfig): S => {
    // Reset the whole state, back to (almost) nothing
    if (action.version === FORCE_CACHE_CLEAR_VERSION) {
        return {
            ...resolveFreshState(contextConfig.retainedOnCacheClear, state) as S,
            actionStatus: {},
            dataVersion: action.version
        }
    }
    // Simply reset actionStatus, prompting all queries to be redownloaded when needed
    return {
        ...state,
        actionStatus: {},
        dataVersion: action.version
    }
}

// Either once an action has come back from API, or doesn't involve an API call.
export const treatLocalAction = <S extends BasicContextState = BasicContextState>(
    state: S, action: AnyAction, config = defaultContextConfig): S => {
    if ('apiResponse' in action) {
        return treatApiResponseAction(state, action, config)
    } else {
        return treatLocalPrimaryAction(state, action, config)
    }
}

const treatApiResponseAction = <S extends BasicContextState = BasicContextState>(
    state: S, action: ApiResponseAction, config = defaultContextConfig): S => {
    if (action.apiResponse?.version) {
        // On any response from the API, check if the site's data has been updated.
        setTimeout(() => action.receiveDataVersion?.(action), 1)
    }

    // Receiving in-depth details for one or many items.
    if (LOAD_DETAILS_ACTION_TYPES.includes(action.type)) {
        return treatDetailsRequestAction(state, action as FullApiResponseAction<LoadDetailsAction>, config)

        // Receiving multiple items' IDs only.
    } else if (LOAD_IDS_ACTION_TYPES.includes(action.type)) {
        return treatIdsRequestAction(state, action as FullApiResponseAction<LoadIdsAction>)
    } else if (action.type.startsWith('GRAPH_')) {
        delete state.inProgress?.[actionKey(action)]
        const { apiResponse } = action
        const graphValue = !apiResponse
            ? null
            : 'graphType' in apiResponse
                ? apiResponse as GraphData // interactive JSON data
                : 'data' in apiResponse ? apiResponse.data : null // legacy SVG/PNG string
        return {
            ...state,
            graphs: {
                ...(state as GraphContextState).graphs,
                [actionKey(action)]: graphValue
            }
        }
    }

    // On any API request success, do things like removing failure logs for the request.
    if (action.type === 'API_REQUEST_SUCCEEDED') {
        return treatSuccessfulApiRequest(state, action)
    } else if (action.type === 'API_REQUEST_FAILED') {
        return treatFailedApiRequest(state, action, config)
    }

    return state
}

const treatLocalPrimaryAction = <S extends BasicContextState = BasicContextState>(
    state: S, action: PrimaryAction, config = defaultContextConfig): S => {
    switch (action.type) {
        case 'EXPIRE_COOLDOWN': {
            // Only clear a genuinely-elapsed cooldown; never clobber a newer deadline or an in-flight request.
            const cooldown = state.inProgress?.[action.key]
            if (typeof cooldown === 'number' && cooldown <= Date.now()) {
                delete state.inProgress?.[action.key]
            }
            return { ...state } // a fresh reference re-renders so the timestamp gate re-evaluates
        }
        case 'FIRE_DELAYED':
            // Run the delayed action only if its deadline is still the active cooldown (not cancelled or superseded).
            if (state.inProgress && state.inProgress[action.key] === action.deadline) {
                state.inProgress[action.key] = 'requestSent'
                setTimeout(() => action.redispatch(action.delayedAction), 0)
            }
            return { ...state }
        case 'SET_IN_PROGRESS':
            if (action.status === undefined) {
                delete state.inProgress?.[actionKey(action.key)]
                return { ...state }
            }
            return {
                ...state,
                inProgress: {
                    ...state.inProgress,
                    [actionKey(action.key)]: action.status
                }
            }
        case 'CLEAR_ACTION_STATUS':
            delete state.actionStatus?.[actionKey(action.actionToClear)]
            return { ...state }

        case 'CLEAR_CACHE':
            return clearSiteCaches(state, action, config)

        case 'CLEAR_GRAPH':
            if (!('graphs' in state))
                return state
            delete (state as GraphContextState).graphs?.[action.graph]
            return { ...state }

        case 'CLEAR_FAILED_ACTION':
            delete state?.failed?.[actionKey(action.action)]
            if ('completedIdsByQuery' in state) {
                delete (state as unknown as { completedIdsByQuery: Record<string, unknown> })
                    .completedIdsByQuery?.[actionKey(action.action)]
            }
            return { ...state }

        case 'RECEIVE_DATA_VERSION':
            return { ...state, dataVersion: action.version }

        default:
            return state
    }
}

const treatLoadDetailsAction = (action: LoadDetailsAction, dispatchToApi: DispatchToApi, config: ContextConfig): void | Promise<void> => {
    if (Array.isArray(action.items)) {
        const idField = typeof config.secondaryIdFields === 'object' && config.idField in config.secondaryIdFields
            ? `&id-field=${config.idField}`
            : ''
        return dispatchToApi(`${config.apiEndpointDetails}?ids=${action.items.join(',')}${idField}`)
    } else {
        return dispatchToApi(`${config.apiEndpointDetails}/${action[config.idField]}`)
    }
}

const treatLoadIdsAction = (action: LoadIdsAction, dispatchToApi: DispatchToApi, config: ContextConfig): void | Promise<void> => {
    const offset = action.offset ? `&offset=${action.offset}` : ''
    const caseSensitive = action.caseSensitive ? '&caseSensitive=1' : ''
    const ordering = [action.order && `order=${action.order}`, action.direction && `direction=${action.direction}`]
        .filter(Boolean).map(s => `&${s}`).join('')
    const dateRange = [action.from && `from=${action.from}`, action.until && `until=${action.until}`]
        .filter(Boolean).map(s => `&${s}`).join('')
    if (action.value && action.comparison) {
        return dispatchToApi(`${config.apiEndpointIds}?field=${action.field}`
          + `&comparison=${action.comparison}&value=${encodeURIComponent(action.value)}${caseSensitive}${offset}${ordering}${dateRange}`)
    } else if (action.value) {
        return dispatchToApi(`${config.apiEndpointIds}?field=${action.field}&value=${action.value}`
          + `${caseSensitive}${offset}${ordering}${dateRange}`)
    } else {
        return dispatchToApi(`${config.apiEndpointIds}?field=${action.field}${caseSensitive}${offset}${ordering}${dateRange}`)
    }
}

const treatSuccessfulApiRequest = <S extends BasicContextState = BasicContextState>(state: S, action: ApiRequestSucceededAction): S => {
    const sourceAction = actionKey(action)
    delete state.failed?.[sourceAction]
    if (action.sourceAction.statusResetDelay) {
        state.inProgress = state.inProgress || {}
        const delayedAction = action.sourceAction.statusResetDelayFor
            ? actionKey(action.sourceAction.statusResetDelayFor)
            : sourceAction
        if (delayedAction !== sourceAction && typeof state.inProgress[sourceAction] !== 'number') {
            delete state.inProgress?.[sourceAction]
        }
        // Cool down to a wall-clock deadline (robust to throttled background timers); the timer only pokes a re-render.
        setTimeout(
            () => action.redispatch({ type: 'EXPIRE_COOLDOWN', key: delayedAction }),
            action.sourceAction.statusResetDelay * 1000)
        state.inProgress[delayedAction] = Date.now() + action.sourceAction.statusResetDelay * 1000
    } else {
        delete state.inProgress?.[sourceAction]
    }
    return { ...state }
}

// Back off hard on server errors (often rate-limiting) so we don't hammer a struggling backend; auto-recovers.
const SERVER_ERROR_RETRY_SECONDS = 15

const treatFailedApiRequest = <S extends InfiniteScrollContextState = InfiniteScrollContextState>(state: S,
    action: ApiRequestFailedAction,
    config: ContextConfig | (ContextConfig & InfiniteScrollConfig)): S => {
    const sourceActionKey = actionKey(action)
    if (action.status >= 400 && action.status < 500) {
        state.failed = {
            ...state.failed,
            [sourceActionKey]: action.status
        }
    }
    if (state.inProgress && sourceActionKey in state.inProgress) {
        // Wall-clock cooldown gates retries; the timer only pokes a re-render. 5xx backs off much further than a normal retry.
        const retryDelay = action.status >= 500 ? SERVER_ERROR_RETRY_SECONDS : AUTOSAVE_INTERVAL_FAILED
        setTimeout(
            () => action.redispatch({ type: 'EXPIRE_COOLDOWN', key: sourceActionKey }),
            retryDelay * 1000)
        state.inProgress[sourceActionKey] = Date.now() + retryDelay * 1000
    }
    if (action.status === 404 && 'renderThumbnail' in config) {
        state = markActionStatus(state, action, 'complete')
    }
    return { ...state }
}

/** Defaults for when any action is first dispatched. */
export const treatApiAction = (action: AnyAction, dispatch: Dispatch<AnyAction>,
    dispatchToApi: DispatchToApi, config = defaultContextConfig): void => {
    if (LOAD_IDS_ACTION_TYPES.includes(action.type)) {
        void treatLoadIdsAction(action as LoadIdsAction, dispatchToApi, config)
    } else if (LOAD_DETAILS_ACTION_TYPES.includes(action.type)) {
        void treatLoadDetailsAction(action as LoadDetailsAction, dispatchToApi, config)
    } else {
        dispatch(action)
    }
}
